import React, { useState, useMemo, useEffect, useRef } from "react";
import { STATUS, UNASSIGNED } from "../constants/colors";

const parseRange = (input) => {
  const out = new Set();
  for (const part of input.split(/[,\s]+/).filter(Boolean)) {
    const m = part.match(/^(\d+)(?:-(\d+))?$/);
    if (!m) continue;
    const a = parseInt(m[1], 10);
    const b = m[2] ? parseInt(m[2], 10) : a;
    for (let i = Math.min(a, b); i <= Math.max(a, b); i++) {
      if (i >= 1 && i <= 400) out.add(i);
    }
  }
  return Array.from(out).sort((a, b) => a - b);
};

const Swatch = ({ s, label }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="inline-block w-4 h-4 rounded-sm" style={{ background: s.bg, boxShadow: `inset 0 0 0 1px ${s.outline}` }} aria-hidden />
    {label}
  </span>
);

export const AssignTab = ({ tickets, users, onAssign, onUnassign, onOpenTicket, showToast }) => {
  const sellers = useMemo(() => users.filter((u) => u.role === "seller"), [users]);
  const [sellerId, setSellerId] = useState("");
  const [range, setRange] = useState("");
  const [err, setErr] = useState("");
  // Filter: null = show all; "Unassigned" = only unassigned; "<sellerName>" = only that seller's tickets
  const [filterName, setFilterName] = useState(null);
  // Combobox state for the filter picker
  const [filterQuery, setFilterQuery] = useState("");
  const [comboOpen, setComboOpen] = useState(false);
  const comboRef = useRef(null);

  const assignedSummary = useMemo(() => {
    const map = {};
    for (let i = 1; i <= 400; i++) {
      const t = tickets[i];
      const name = t?.assigned_seller_name || "Unassigned";
      map[name] = (map[name] || 0) + 1;
    }
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [tickets]);

  const filterCount = filterName != null ? (assignedSummary.find(([n]) => n === filterName)?.[1] || 0) : 0;

  const filteredSummary = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) return assignedSummary;
    return assignedSummary.filter(([name]) => name.toLowerCase().includes(q));
  }, [assignedSummary, filterQuery]);

  // Close the combobox dropdown when clicking outside it.
  useEffect(() => {
    if (!comboOpen) return;
    const onDocClick = (e) => {
      if (comboRef.current && !comboRef.current.contains(e.target)) {
        setComboOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [comboOpen]);

  const doAssign = async () => {
    setErr("");
    const numbers = parseRange(range);
    if (!sellerId) { setErr("Select a seller."); return; }
    if (numbers.length === 0) { setErr("Enter ticket numbers like '1-50' or '1, 5, 10'."); return; }
    const res = await onAssign(numbers, sellerId);
    if (!res.ok) { setErr(res.error); return; }
    showToast(`${numbers.length} ticket${numbers.length === 1 ? "" : "s"} assigned`);
    setRange("");
  };

  const doUnassign = async () => {
    setErr("");
    const numbers = parseRange(range);
    if (numbers.length === 0) { setErr("Enter ticket numbers."); return; }
    const res = await onUnassign(numbers);
    if (!res.ok) { setErr(res.error); return; }
    showToast(`${numbers.length} ticket${numbers.length === 1 ? "" : "s"} unassigned`);
    setRange("");
  };

  const inputCls =
    "w-full rounded-md border bg-[color:var(--surface-card)] px-3 py-2 text-base transition-colors focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25";

  const selectFilter = (name) => {
    setFilterName(name);
    setFilterQuery("");
    setComboOpen(false);
  };

  const clearFilter = () => {
    setFilterName(null);
    setFilterQuery("");
    setComboOpen(false);
  };

  // A ticket matches the active filter when:
  //   filterName === null       → all match
  //   filterName === "Unassigned" → only if no assigned_seller_id
  //   else                        → only if assigned_seller_name === filterName
  const matchesFilter = (t) => {
    if (filterName == null) return true;
    if (filterName === "Unassigned") return !t?.assigned_seller_id;
    return (t?.assigned_seller_name || "") === filterName;
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-[color:var(--surface-card)] border rounded-lg p-5" style={{ borderColor: "var(--border-default)" }}>
          <h2 className="text-base font-semibold mb-1 text-[color:var(--ink-primary)]">Assign tickets to a seller</h2>
          <p className="text-sm mb-4 text-[color:var(--ink-tertiary)]">
            Use <strong>Assign</strong> to give ticket(s) to the selected seller (overwrites any prior owner). Use <strong>Unassign</strong> to remove ownership — no seller needs to be selected. Or just <strong>tap any ticket below</strong> to open the full ticket details, where admins can also reassign.
          </p>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block text-[color:var(--ink-primary)]">Seller</label>
              <select value={sellerId} onChange={(e) => setSellerId(e.target.value)} data-testid="assign-seller" className={inputCls} style={{ borderColor: "var(--border-default)" }}>
                <option value="">— select seller —</option>
                {sellers.map((s) => {
                  const label = `${s.name} (${s.email})`;
                  return <option key={s.id} value={s.id}>{label}</option>;
                })}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block text-[color:var(--ink-primary)]">Ticket numbers</label>
              <input
                data-testid="assign-range"
                value={range}
                onChange={(e) => setRange(e.target.value)}
                placeholder="e.g. 1-50, 99, 101-110"
                className={inputCls}
                style={{ borderColor: "var(--border-default)" }}
              />
              <p className="text-xs mt-1.5 text-[color:var(--ink-tertiary)]">Use ranges (1-50) and/or comma-separated numbers.</p>
            </div>
            {err && <p className="text-sm font-medium text-rose-700">{err}</p>}
            <div className="flex gap-2">
              <button data-testid="assign-btn" onClick={doAssign} className="px-5 py-2.5 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90" style={{ background: "var(--semantic-success)" }}>Assign</button>
              <button data-testid="unassign-btn" onClick={doUnassign} className="px-5 py-2.5 text-sm font-medium rounded-md border bg-[color:var(--surface-card)] transition-colors hover:border-rose-500 hover:text-rose-700" style={{ borderColor: "var(--border-default)", color: "var(--ink-primary)" }}>Unassign</button>
            </div>
          </div>
        </div>

        <div className="bg-[color:var(--surface-card)] border rounded-lg p-5" style={{ borderColor: "var(--border-default)" }}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold text-[color:var(--ink-primary)]">Filter grid by seller</h2>
            {filterName != null && (
              <button
                data-testid="filter-clear-top"
                onClick={clearFilter}
                className="text-xs font-semibold text-[color:var(--brand-primary)] hover:underline"
              >
                Clear
              </button>
            )}
          </div>

          <div className="relative" ref={comboRef}>
            <input
              data-testid="assign-filter-combobox"
              type="text"
              value={comboOpen || filterName == null ? filterQuery : filterName}
              onChange={(e) => { setFilterQuery(e.target.value); setComboOpen(true); }}
              onFocus={() => { setComboOpen(true); if (filterName != null) setFilterQuery(""); }}
              placeholder={filterName == null ? "Type a name — or pick from the list…" : "Type a name to change filter…"}
              className={inputCls}
              style={{ borderColor: "var(--border-default)" }}
              autoComplete="off"
            />
            {comboOpen && (
              <div
                data-testid="assign-filter-dropdown"
                className="absolute z-20 mt-1 w-full bg-[color:var(--surface-card)] border rounded-md shadow-lg max-h-72 overflow-y-auto"
                style={{ borderColor: "var(--border-default)" }}
              >
                <button
                  type="button"
                  data-testid="assign-filter-option-all"
                  onClick={clearFilter}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--surface-subtle)] flex items-center justify-between ${filterName == null ? "bg-[color:var(--brand-primary)]/10 font-semibold" : ""}`}
                >
                  <span>All tickets</span>
                  <span className="text-xs text-[color:var(--ink-tertiary)]">400</span>
                </button>
                {filteredSummary.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-[color:var(--ink-tertiary)]">No sellers match &ldquo;{filterQuery}&rdquo;.</div>
                ) : (
                  filteredSummary.map(([name, count]) => {
                    const isActive = filterName === name;
                    return (
                      <button
                        type="button"
                        key={name}
                        data-testid={`assign-filter-option-${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "unassigned"}`}
                        onClick={() => selectFilter(name)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--surface-subtle)] flex items-center justify-between ${isActive ? "bg-[color:var(--brand-primary)]/10 font-semibold text-[color:var(--brand-primary)]" : ""}`}
                      >
                        <span className={name === "Unassigned" ? "text-[color:var(--ink-tertiary)]" : "text-[color:var(--ink-primary)]"}>{name}</span>
                        <span className="text-xs text-[color:var(--ink-tertiary)]">{count}</span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <p className="mt-3 text-xs text-[color:var(--ink-tertiary)]">
            {filterName == null
              ? `${sellers.length} seller${sellers.length === 1 ? "" : "s"} total. Start typing to find one, or click the input to see all.`
              : `Showing tickets assigned to ${filterName}.`}
          </p>
        </div>
      </div>

      <div className="mt-5 bg-[color:var(--surface-card)] border rounded-lg p-5" style={{ borderColor: "var(--border-default)" }}>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-sm font-semibold text-[color:var(--ink-primary)]">Tickets coloured by status</h3>
          <div className="flex items-center gap-4 flex-wrap text-xs text-[color:var(--ink-tertiary)]">
            <Swatch s={STATUS.available} label="Available" />
            <Swatch s={STATUS.reserved} label="Reserved" />
            <Swatch s={STATUS.sold} label="Sold" />
            <Swatch s={UNASSIGNED} label="Unassigned" />
          </div>
        </div>

        {filterName != null && (
          <div
            data-testid="filter-banner"
            className="mb-3 flex items-center justify-between gap-2 bg-[color:var(--brand-primary)]/10 border border-[color:var(--brand-primary)]/30 rounded-md px-3 py-2"
          >
            <p className="text-sm text-[color:var(--ink-primary)]">
              <span className="text-[color:var(--ink-tertiary)]">Showing only:</span>{" "}
              <span className="font-semibold text-[color:var(--brand-primary)]">{filterName}</span>
              <span className="text-[color:var(--ink-tertiary)]"> · {filterCount} ticket{filterCount === 1 ? "" : "s"}</span>
            </p>
            <button
              data-testid="filter-clear"
              onClick={clearFilter}
              className="text-xs font-semibold text-[color:var(--brand-primary)] hover:underline"
            >
              Clear filter
            </button>
          </div>
        )}

        <div className="grid gap-1 grid-cols-10 sm:grid-cols-[repeat(20,minmax(0,1fr))]">
          {Array.from({ length: 400 }, (_, i) => i + 1)
            .filter((n) => matchesFilter(tickets[n] || { status: "available" }))
            .map((n) => {
              const t = tickets[n] || { status: "available" };
              const assigned = !!t.assigned_seller_id;
              const s = assigned ? STATUS[t.status] : UNASSIGNED;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => onOpenTicket?.(n)}
                  data-testid={`assign-cell-${n}`}
                  title={`#${n} — ${t.assigned_seller_name || "Unassigned"} (${t.status})`}
                  className="relative aspect-square flex items-center justify-center rounded-md font-bold transition-transform duration-100 hover:scale-[1.15] hover:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-primary)]"
                  style={{
                    background: s.bg,
                    color: s.text,
                    fontSize: "11px",
                    lineHeight: 1,
                    boxShadow: `inset 0 0 0 1px ${s.outline}`,
                  }}
                >
                  {n}
                </button>
              );
            })}
          {filterName != null && filterCount === 0 && (
            <div className="col-span-full text-center text-sm py-12 text-[color:var(--ink-tertiary)]" data-testid="assign-grid-empty">
              No tickets match this filter.
            </div>
          )}
        </div>
        <p className="mt-3 text-xs text-[color:var(--ink-tertiary)]">
          Tap any ticket to open its full details — admins can reassign it directly from that modal.
        </p>
      </div>
    </section>
  );
};
